import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { IMAGE_ACCEPT_ATTRIBUTE } from '../../../shared/constants/uploadConstraints';

type CropHandle = 'move' | 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const MIN_CROP_RATIO = 0.05;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const extractClipboardFiles = (event: ClipboardEvent) => {
  const items = Array.from(event.clipboardData?.items ?? []).map((item) => item.getAsFile());
  const files = Array.from(event.clipboardData?.files ?? []);
  return [...items, ...files].filter((file): file is File => Boolean(file));
};

const isEditableElement = (element: Element | null): boolean => {
  if (!element) {
    return false;
  }

  const tagName = element.tagName?.toLowerCase();
  if (tagName === 'input' || tagName === 'textarea') {
    return true;
  }

  if ((element as HTMLElement).isContentEditable) {
    return true;
  }

  return false;
};

const fileMatchesAccept = (file: File, accept: string) => {
  if (!accept) {
    return true;
  }

  const acceptList = accept
    .split(',')
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);

  if (!acceptList.length) {
    return true;
  }

  const fileType = file.type.toLowerCase();
  const extension = (() => {
    const name = file.name?.toLowerCase() ?? '';
    const dotIndex = name.lastIndexOf('.');
    if (dotIndex === -1) {
      return '';
    }
    return name.slice(dotIndex);
  })();

  return acceptList.some((rule) => {
    if (rule === fileType) {
      return true;
    }
    if (rule.endsWith('/*')) {
      const prefix = rule.slice(0, -1);
      return fileType.startsWith(prefix);
    }
    if (rule.startsWith('.')) {
      return extension === rule;
    }
    return false;
  });
};

const normalizeRect = (rect: CropRect): CropRect => {
  let { x, y, width, height } = rect;
  width = Math.max(width, MIN_CROP_RATIO);
  height = Math.max(height, MIN_CROP_RATIO);

  if (x < 0) {
    width += x;
    x = 0;
  }

  if (y < 0) {
    height += y;
    y = 0;
  }

  if (x + width > 1) {
    width = 1 - x;
  }

  if (y + height > 1) {
    height = 1 - y;
  }

  width = Math.max(width, MIN_CROP_RATIO);
  height = Math.max(height, MIN_CROP_RATIO);

  if (x + width > 1) {
    x = 1 - width;
  }

  if (y + height > 1) {
    y = 1 - height;
  }

  return { x, y, width, height };
};

const computeCropRect = (handle: CropHandle, startRect: CropRect, deltaX: number, deltaY: number): CropRect => {
  let { x, y, width, height } = startRect;

  switch (handle) {
    case 'move':
      x = clamp(startRect.x + deltaX, 0, 1 - width);
      y = clamp(startRect.y + deltaY, 0, 1 - height);
      break;
    case 'n': {
      const newY = clamp(startRect.y + deltaY, 0, startRect.y + startRect.height - MIN_CROP_RATIO);
      const newHeight = startRect.height + (startRect.y - newY);
      y = newY;
      height = clamp(newHeight, MIN_CROP_RATIO, 1 - y);
      break;
    }
    case 's': {
      const newHeight = clamp(startRect.height + deltaY, MIN_CROP_RATIO, 1 - startRect.y);
      height = newHeight;
      break;
    }
    case 'w': {
      const newX = clamp(startRect.x + deltaX, 0, startRect.x + startRect.width - MIN_CROP_RATIO);
      const newWidth = startRect.width + (startRect.x - newX);
      x = newX;
      width = clamp(newWidth, MIN_CROP_RATIO, 1 - x);
      break;
    }
    case 'e': {
      const newWidth = clamp(startRect.width + deltaX, MIN_CROP_RATIO, 1 - startRect.x);
      width = newWidth;
      break;
    }
    case 'nw': {
      const newX = clamp(startRect.x + deltaX, 0, startRect.x + startRect.width - MIN_CROP_RATIO);
      const newWidth = startRect.width + (startRect.x - newX);
      const newY = clamp(startRect.y + deltaY, 0, startRect.y + startRect.height - MIN_CROP_RATIO);
      const newHeight = startRect.height + (startRect.y - newY);
      x = newX;
      y = newY;
      width = clamp(newWidth, MIN_CROP_RATIO, 1 - x);
      height = clamp(newHeight, MIN_CROP_RATIO, 1 - y);
      break;
    }
    case 'ne': {
      const newWidth = clamp(startRect.width + deltaX, MIN_CROP_RATIO, 1 - startRect.x);
      const newY = clamp(startRect.y + deltaY, 0, startRect.y + startRect.height - MIN_CROP_RATIO);
      const newHeight = startRect.height + (startRect.y - newY);
      y = newY;
      width = newWidth;
      height = clamp(newHeight, MIN_CROP_RATIO, 1 - y);
      break;
    }
    case 'sw': {
      const newX = clamp(startRect.x + deltaX, 0, startRect.x + startRect.width - MIN_CROP_RATIO);
      const newWidth = startRect.width + (startRect.x - newX);
      const newHeight = clamp(startRect.height + deltaY, MIN_CROP_RATIO, 1 - startRect.y);
      x = newX;
      width = clamp(newWidth, MIN_CROP_RATIO, 1 - x);
      height = newHeight;
      break;
    }
    case 'se': {
      const newWidth = clamp(startRect.width + deltaX, MIN_CROP_RATIO, 1 - startRect.x);
      const newHeight = clamp(startRect.height + deltaY, MIN_CROP_RATIO, 1 - startRect.y);
      width = newWidth;
      height = newHeight;
      break;
    }
    default:
      break;
  }

  x = clamp(x, 0, 1 - width);
  y = clamp(y, 0, 1 - height);

  return normalizeRect({ x, y, width, height });
};

export interface UploadPanelProps {
  accept?: string;
  disabled?: boolean;
  isUploading?: boolean;
  onSelectFile: (file: File) => void;
  onClearSelectedFile?: () => void;
  previewUrl?: string | null;
  previewName?: string | null;
  previewMeta?: string | null;
  selectedStylePreviewUrl?: string | null;
  selectedStyleName?: string | null;
  selectedStyleBackground?: string | null;
  onClearSelectedStyle?: () => void;
  selectedFile?: File | null;
  convertedFromHeic?: boolean;
  isPreparingImage?: boolean;
  preparingMessage?: string | null;
  onSubmitSelection?: () => void;
  onCancelUpload?: () => void;
  countdownSeconds?: number | null;
  showExtendedWaitNotice?: boolean;
  canSubmit?: boolean;
  costNotice?: string | null;
  costNoticeTone?: 'default' | 'warning' | 'danger';
  showError?: boolean;
  errorMessage?: string | null;
  onRetryError?: (() => void) | null;
}

export function UploadPanel({
  accept = IMAGE_ACCEPT_ATTRIBUTE,
  disabled,
  isUploading,
  onSelectFile,
  onClearSelectedFile,
  previewUrl,
  previewName,
  previewMeta,
  selectedStylePreviewUrl,
  selectedStyleName,
  selectedStyleBackground,
  onClearSelectedStyle,
  selectedFile = null,
  convertedFromHeic = false,
  isPreparingImage = false,
  preparingMessage = null,
  onSubmitSelection,
  onCancelUpload,
  countdownSeconds = null,
  showExtendedWaitNotice = false,
  canSubmit = false,
  costNotice,
  costNoticeTone = 'default',
  showError = false,
  errorMessage,
  onRetryError
}: UploadPanelProps) {
  const { t } = useTranslation();
  const selectLabel = isUploading ? t('uploadPanel.uploading') : t('uploadPanel.selectButton');
  const replaceLabelRaw = t('uploadPanel.replaceButton');
  const replaceLabel =
    replaceLabelRaw === 'uploadPanel.replaceButton' ? selectLabel : replaceLabelRaw;
  const previewAltRaw = t('uploadPanel.previewAlt');
  const previewAlt =
    previewAltRaw === 'uploadPanel.previewAlt'
      ? t('guestPage.preview.alt', { name: previewName ?? t('uploadPanel.selectButton') })
      : previewAltRaw;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setDragging] = useState(false);
  const [isCropping, setCropping] = useState(false);
  const [cropRect, setCropRect] = useState<CropRect>({ x: 0, y: 0, width: 1, height: 1 });
  const [cropImageSize, setCropImageSize] = useState<{ width: number; height: number } | null>(null);
  const [isApplyingCrop, setApplyingCrop] = useState(false);
  const cropImageRef = useRef<HTMLImageElement | null>(null);
  const cropContainerRef = useRef<HTMLDivElement | null>(null);
  const cropDialogRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<{
    handle: CropHandle;
    startRect: CropRect;
    startPointer: { x: number; y: number };
    containerRect: DOMRect;
  } | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const previousOverflowRef = useRef<string>('');

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handlePaste = (event: ClipboardEvent) => {
      if (disabled) {
        return;
      }

      if (isEditableElement(event.target as Element | null)) {
        return;
      }

      const files = extractClipboardFiles(event);
      const file = files.find((candidate) => fileMatchesAccept(candidate, accept));
      if (!file) {
        return;
      }

      event.preventDefault();
      onSelectFile(file);
    };

    window.addEventListener('paste', handlePaste);
    return () => {
      window.removeEventListener('paste', handlePaste);
    };
  }, [accept, disabled, onSelectFile]);

  const stylePreviewBackgroundStyle = selectedStyleBackground
    ? { background: selectedStyleBackground }
    : undefined;

  const stylePreview = selectedStylePreviewUrl ? (
    <div className="relative mt-0 flex w-full flex-col items-center gap-2">
      <div className="relative aspect-square w-36 overflow-hidden rounded-[1.5rem] border border-ai-surface-glow/30 bg-ai-surface/50 shadow-ai-card sm:w-50">
        {onClearSelectedStyle ? (
          <button
            type="button"
            className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-ai-surface-elevated/90 text-sm font-semibold text-ai-text-secondary shadow-ai-card transition hover:bg-ai-primary/80 hover:text-white focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
            onClick={(event) => {
              event.stopPropagation();
              onClearSelectedStyle();
            }}
            aria-label={t('uploadPanel.clearSelectedStyleLabel')}
          >
            <span aria-hidden="true">×</span>
          </button>
        ) : null}
        <div className="flex h-full w-full items-center justify-center bg-ai-surface-elevated/80" style={stylePreviewBackgroundStyle}>
          <img
            src={selectedStylePreviewUrl}
            alt={t('styleSelector.previewAlt', {
              style: selectedStyleName ?? t('uploadPanel.selectButton')
            })}
            className="h-full w-full rounded-[1.5rem] object-cover"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  ) : null;

  useEffect(() => {
    if (!isCropping) {
      return;
    }
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    previousOverflowRef.current = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    cropDialogRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflowRef.current;
      previousFocusRef.current?.focus();
    };
  }, [isCropping]);

  const handleCropPointerMove = useCallback((event: PointerEvent) => {
    const dragState = dragStateRef.current;
    if (!dragState) return;
    event.preventDefault();
    const { containerRect, startRect, handle, startPointer } = dragState;
    if (!containerRect.width || !containerRect.height) {
      return;
    }
    const deltaX = (event.clientX - startPointer.x) / containerRect.width;
    const deltaY = (event.clientY - startPointer.y) / containerRect.height;
    setCropRect(computeCropRect(handle, startRect, deltaX, deltaY));
  }, []);

  const handleCropPointerUp = useCallback(() => {
    dragStateRef.current = null;
    window.removeEventListener('pointermove', handleCropPointerMove);
    window.removeEventListener('pointerup', handleCropPointerUp);
  }, [handleCropPointerMove]);

  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', handleCropPointerMove);
      window.removeEventListener('pointerup', handleCropPointerUp);
    };
  }, [handleCropPointerMove, handleCropPointerUp]);

  const startCropInteraction = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>, handle: CropHandle) => {
      if (!cropContainerRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      const containerRect = cropContainerRef.current.getBoundingClientRect();
      dragStateRef.current = {
        handle,
        startRect: cropRect,
        startPointer: { x: event.clientX, y: event.clientY },
        containerRect
      };
      window.addEventListener('pointermove', handleCropPointerMove);
      window.addEventListener('pointerup', handleCropPointerUp);
    },
    [cropRect, handleCropPointerMove, handleCropPointerUp]
  );

  const closeCropper = useCallback(() => {
    setCropping(false);
    setApplyingCrop(false);
    window.removeEventListener('pointermove', handleCropPointerMove);
    window.removeEventListener('pointerup', handleCropPointerUp);
    dragStateRef.current = null;
  }, [handleCropPointerMove, handleCropPointerUp]);

  useEffect(() => {
    if (!isCropping) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeCropper();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isCropping, closeCropper]);

  const openCropper = useCallback(() => {
    if (!selectedFile || !previewUrl) return;
    setCropRect({ x: 0, y: 0, width: 1, height: 1 });
    setCropImageSize(null);
    setApplyingCrop(false);
    dragStateRef.current = null;
    setCropping(true);
  }, [selectedFile, previewUrl]);

  const canvasToBlob = useCallback(
    (canvas: HTMLCanvasElement, mimeType: string) =>
      new Promise<Blob | null>((resolve) => {
        if (mimeType === 'image/jpeg') {
          canvas.toBlob(resolve, mimeType, 0.92);
          return;
        }
        canvas.toBlob(resolve, mimeType);
      }),
    []
  );

  const applyCrop = useCallback(async () => {
    if (!selectedFile || !cropImageRef.current || !cropImageSize) {
      closeCropper();
      return;
    }
    setApplyingCrop(true);
    try {
      const { width: naturalWidth, height: naturalHeight } = cropImageSize;
      const { x, y, width, height } = cropRect;
      const sourceX = Math.round(x * naturalWidth);
      const sourceY = Math.round(y * naturalHeight);
      const sourceWidth = Math.max(1, Math.round(width * naturalWidth));
      const sourceHeight = Math.max(1, Math.round(height * naturalHeight));

      const canvas = document.createElement('canvas');
      canvas.width = sourceWidth;
      canvas.height = sourceHeight;

      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('Canvas context unavailable');
      }

      context.drawImage(
        cropImageRef.current,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        sourceWidth,
        sourceHeight
      );

      const preferredMime = selectedFile.type || 'image/png';
      let blob = await canvasToBlob(canvas, preferredMime);
      if (!blob) {
        blob = await canvasToBlob(canvas, 'image/png');
      }
      if (!blob) {
        throw new Error('Failed to crop image');
      }

      const originalName = selectedFile.name;
      const extensionIndex = originalName.lastIndexOf('.');
      const croppedName =
        extensionIndex > 0
          ? `${originalName.slice(0, extensionIndex)}-cropped${originalName.slice(extensionIndex)}`
          : `${originalName}-cropped`;

      const croppedFile = new File([blob], croppedName, { type: blob.type || preferredMime });
      onSelectFile(croppedFile);
      closeCropper();
    } catch (error) {
      console.warn('Crop failed', error);
      closeCropper();
    } finally {
      setApplyingCrop(false);
    }
  }, [selectedFile, cropImageSize, cropRect, canvasToBlob, onSelectFile, closeCropper]);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files?.length) return;
      const file = files[0];
      onSelectFile(file);
    },
    [onSelectFile]
  );

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (disabled) return;
      setDragging(false);
      handleFiles(event.dataTransfer.files);
    },
    [disabled, handleFiles]
  );

  const onDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setDragging(true);
  }, [disabled]);

  const onDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
  }, []);

  const triggerFileDialog = useCallback(() => {
    inputRef.current?.click();
  }, []);
  const costNoticeClassName = clsx(
    'mt-3 text-xs justify-center',
    costNoticeTone === 'warning'
      ? 'text-ai-warning'
      : costNoticeTone === 'danger'
      ? 'text-ai-danger'
      : 'text-ai-text-secondary/80'
  );

  return (
    <>
      <div className="w-full">
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="sr-only"
          disabled={disabled}
          onChange={(event) => {
            handleFiles(event.target.files);
            // Reset value so picking the same file again still triggers onChange
            event.target.value = '';
          }}
          aria-hidden
        />
        <div
          className={clsx(
            'relative overflow-hidden rounded-xl border-4 border-dashed border-ai-surface-glow/40 bg-ai-surface-elevated/60 shadow-ai-card transition-all duration-300',
            disabled && 'cursor-not-allowed',
            !disabled && 'hover:border-ai-primary/60 hover:shadow-ai-glow',
            isDragging && 'border-ai-primary/80 bg-ai-surface-elevated/80'
          )}
        >
          <div
            role="button"
            tabIndex={0}
            onClick={disabled ? undefined : triggerFileDialog}
            onKeyDown={(event) => {
              if (disabled) return;
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                triggerFileDialog();
              }
            }}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            className={clsx(
              'group relative flex min-h-[220px] w-full flex-col items-center justify-center gap-1 p-2 text-center transition-all duration-300',
              !disabled && 'cursor-pointer'
            )}
            aria-disabled={disabled}
          >
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-ai-primary/10 via-transparent to-ai-secondary/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            {isPreparingImage ? (
              <div
                className="mb-4 flex items-center gap-2 rounded-full border border-ai-warning/60 bg-ai-warning/10 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.32em] text-ai-warning shadow-ai-card"
                role="status"
                aria-live="polite"
              >
                <span className="h-2 w-2 animate-ping rounded-full bg-ai-warning" aria-hidden="true" />
                <span>{preparingMessage ?? t('uploadPanel.conversion.heicInProgress')}</span>
              </div>
            ) : null}
            <div className="whitespace-pre-line flex items-center gap-2 rounded-lg border border-ai-surface-glow/40 bg-ai-secondary/30 px-2 py-1 text-sm uppercase tracking-[1em]">
              {t('uploadPanel.badge')}
            </div>
            {previewUrl ? (
              <div className="relative flex w-full flex-col items-center gap-3">
                <div className="relative inline-flex max-w-full items-center justify-center">
                  <img
                    src={previewUrl}
                    alt={previewName ?? previewAlt}
                    className="block max-h-[320px] w-auto max-w-full rounded-2xl object-contain"
                  />
                  {selectedFile && onClearSelectedFile ? (
                    <button
                      type="button"
                      className="absolute right-2.5 top-2.5 z-[1] flex h-8 w-8 items-center justify-center rounded-full bg-ai-surface-elevated/95 text-base font-semibold text-ai-text-secondary shadow-ai-card transition hover:bg-ai-primary/80 hover:text-white focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (disabled) {
                          return;
                        }
                        onClearSelectedFile();
                      }}
                      aria-label={t('uploadPanel.clearSelectedImageLabel')}
                    >
                      <span aria-hidden="true">×</span>
                    </button>
                  ) : null}
                </div>
                {(previewName || previewMeta || convertedFromHeic) ? (
                  <div className="flex flex-col items-center gap-2 text-xs text-ai-text-secondary">
                    {previewName ? (
                      <div className="rounded-full border border-ai-surface-glow/40 bg-ai-surface/70 px-4 py-1">
                        {previewName}
                      </div>
                    ) : null}
                    {(convertedFromHeic || previewMeta) ? (
                      <div className="flex flex-wrap items-center justify-center gap-2 text-[11px] font-semibold">
                        {convertedFromHeic ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-ai-primary/50 bg-ai-primary/15 px-3 py-1 text-ai-primary shadow-ai-card">
                            {t('uploadPanel.conversion.heicConvertedMeta')}
                          </span>
                        ) : null}
                        {previewMeta ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-ai-surface-glow/40 bg-ai-surface/70 px-3 py-1 text-ai-text-secondary/80">
                            {previewMeta}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                <div className="flex flex-wrap justify-center gap-3">
                  {selectedFile ? (
                    <button
                      type="button"
                      className="rounded-full border border-ai-primary/60 px-6 py-2 text-sm font-semibold text-ai-primary shadow-ai-card transition-colors hover:bg-ai-primary/15 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!disabled) openCropper();
                      }}
                      disabled={disabled}
                    >
                      {t('uploadPanel.cropImageButton')}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="rounded-full bg-ai-primary px-6 py-2 text-sm font-semibold text-white shadow-ai-glow transition-colors hover:bg-ai-secondary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
                    onClick={(event) => {
                      event.stopPropagation();
                      if (!disabled) triggerFileDialog();
                    }}
                    disabled={disabled}
                  >
                    {replaceLabel}
                  </button>
                </div>
            {stylePreview}
                {selectedFile && onSubmitSelection ? (
                  <>
                    {showError ? (
                      <div
                        className="mt-3 w-full max-w-lg rounded-2xl border border-ai-danger/40 bg-ai-surface/80 p-4 text-sm text-ai-danger shadow-ai-card"
                        role="alert"
                        aria-live="assertive"
                      >
                        <div>{errorMessage ?? t('uploadError.default')}</div>
                        {onRetryError ? (
                          <div className="mt-3">
                            <button
                              type="button"
                              className="rounded-full border border-ai-danger/40 px-4 py-1 text-xs font-semibold text-ai-danger transition-colors hover:bg-ai-danger/15 focus:outline-none focus-visible:ring focus-visible:ring-ai-danger/60"
                              onClick={(event) => {
                                event.stopPropagation();
                                onRetryError();
                              }}
                            >
                              {t('uploadError.retry')}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    <div className="mt-1 flex w-full max-w-lg items-center justify-center gap-3">
                  <button
                    type="button"
                    className={clsx(
                      'flex w-full items-center justify-center rounded-full px-5 py-2 text-lg font-semibold transition-transform focus:outline-none focus-visible:ring focus-visible:ring-ai-focus disabled:cursor-not-allowed disabled:opacity-70',
                      isUploading
                        ? 'bg-ai-surface-elevated/90 text-ai-primary shadow-inner ring-1 ring-ai-primary/40 hover:scale-100 cursor-wait disabled:cursor-wait'
                        : 'bg-gradient-to-r from-ai-primary via-ai-secondary to-ai-primary text-white shadow-[0_25px_60px_rgba(99,102,241,0.35)] hover:scale-[1.02]'
                    )}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!disabled) {
                      onSubmitSelection();
                    }
                  }}
                  disabled={disabled || isUploading || !canSubmit}
                  aria-busy={isUploading}
                >
                  <span className="flex items-center gap-2" aria-live="polite">
                    {isUploading ? (
                      <>
                        <svg
                          aria-hidden="true"
                          className="h-5 w-5 animate-spin text-ai-primary"
                          viewBox="0 0 24 24"
                          focusable="false"
                        >
                          <circle
                            className="opacity-20"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-80"
                            d="M22 12a10 10 0 0 0-10-10"
                            stroke="currentColor"
                            strokeWidth="4"
                            strokeLinecap="round"
                            fill="none"
                          />
                        </svg>
                        <span>
                          {countdownSeconds !== null && countdownSeconds > 0
                            ? t('uploadPanel.submit.processingCountdown', {
                                seconds: countdownSeconds
                              })
                            : t('uploadPanel.submit.processing')}
                        </span>
                      </>
                    ) : (
                      <span>{t('uploadPanel.submit.cta')}</span>
                    )}
                  </span>
                </button>
                {isUploading && onCancelUpload ? (
                  <button
                    type="button"
                    className="flex h-12 w-12 items-center justify-center rounded-full border border-ai-primary/40 bg-ai-surface/80 text-ai-primary shadow-ai-card transition-colors hover:bg-ai-primary hover:text-white focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
                    onClick={(event) => {
                      event.stopPropagation();
                      onCancelUpload();
                    }}
                    aria-label={t('uploadPanel.submit.cancel')}
                  >
                    <svg
                      aria-hidden="true"
                      className="h-5 w-5"
                      viewBox="0 0 24 24"
                      focusable="false"
                      fill="currentColor"
                    >
                      <rect x="7" y="7" width="10" height="10" rx="2" />
                    </svg>
                  </button>
                ) : null}
                </div>
                {isUploading && showExtendedWaitNotice ? (
                  <p className="mt-3 text-sm text-ai-text-secondary" role="status" aria-live="polite">
                    {t('uploadPanel.submit.extendedWait')}
                  </p>
                ) : null}
              </>
            ) : null}
            {costNotice ? (
              <div className="mt-0 flex justify-center">
                <p className={costNoticeClassName} role="status" aria-live="polite">
                  {costNotice}
                </p>
              </div>
            ) : null}
          </div>
        ) : (
              <div className="relative flex flex-col items-center gap-3 text-ai-text-secondary">
                <p className="text-lg font-medium text-ai-text-primary">{t('uploadPanel.title')}</p>
                <p className="max-w-md text-sm leading-relaxed">
                  {t('uploadPanel.subtitle')}
                </p>
                <button
                  type="button"
                  className="rounded-full bg-ai-primary px-6 py-2 text-sm font-semibold text-white shadow-ai-glow transition-colors hover:bg-ai-secondary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!disabled) triggerFileDialog();
                  }}
                  disabled={disabled}
                >
                  {selectLabel}
                </button>
                <p className="text-xs text-ai-text-secondary/80">{t('uploadPanel.hint')}</p>
                {stylePreview}
              </div>
            )}
          </div>
        </div>
      </div>
      {isCropping && selectedFile && previewUrl ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label={t('uploadPanel.cropper.title')}
        >
          <div
            ref={cropDialogRef}
            tabIndex={-1}
            className="flex w-full max-w-5xl flex-col gap-6 rounded-3xl bg-ai-surface p-6 shadow-2xl outline-none"
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-ai-text-primary">
                  {t('uploadPanel.cropper.title')}
                </h2>
                <p className="text-sm text-ai-text-secondary">
                  {t('uploadPanel.cropper.instructions')}
                </p>
              </div>
            </div>
            <div className="flex justify-center overflow-hidden rounded-2xl border border-ai-surface-glow/40 bg-ai-surface-elevated/60 p-4">
              <div className="relative inline-block" ref={cropContainerRef}>
                <img
                  ref={cropImageRef}
                  src={previewUrl}
                  alt={previewName ?? previewAlt}
                  className="block max-h-[70vh] w-auto rounded-2xl object-contain"
                  onLoad={(event) => {
                    setCropImageSize({
                      width: event.currentTarget.naturalWidth,
                      height: event.currentTarget.naturalHeight
                    });
                  }}
                  draggable={false}
                />
                <div
                  className="absolute"
                  style={{
                    top: `${cropRect.y * 100}%`,
                    left: `${cropRect.x * 100}%`,
                    width: `${cropRect.width * 100}%`,
                    height: `${cropRect.height * 100}%`
                  }}
                >
                  <div
                    className="relative h-full w-full cursor-move rounded-lg border-2 border-ai-primary/80 shadow-[0_0_0_9999px_rgba(8,15,35,0.65)]"
                    onPointerDown={(event) => startCropInteraction(event, 'move')}
                  >
                    <div
                      className="pointer-events-none h-full w-full rounded-[6px]"
                      style={{
                        backgroundImage:
                          'linear-gradient(0deg, transparent calc(33.333% - 0.5px), rgba(255,255,255,0.35) calc(33.333%), transparent calc(33.333% + 0.5px), transparent calc(66.666% - 0.5px), rgba(255,255,255,0.35) calc(66.666%), transparent calc(66.666% + 0.5px)), linear-gradient(90deg, transparent calc(33.333% - 0.5px), rgba(255,255,255,0.35) calc(33.333%), transparent calc(33.333% + 0.5px), transparent calc(66.666% - 0.5px), rgba(255,255,255,0.35) calc(66.666%), transparent calc(66.666% + 0.5px))'
                      }}
                    />
                    <div
                      className="absolute -left-2 -top-2 h-4 w-4 cursor-nwse-resize rounded-full border border-white bg-ai-primary"
                      aria-hidden="true"
                      onPointerDown={(event) => startCropInteraction(event, 'nw')}
                    />
                    <div
                      className="absolute top-1/2 -left-2 h-4 w-4 -translate-y-1/2 cursor-ew-resize rounded-full border border-white bg-ai-primary"
                      aria-hidden="true"
                      onPointerDown={(event) => startCropInteraction(event, 'w')}
                    />
                    <div
                      className="absolute -left-2 bottom-0 h-4 w-4 translate-y-1/2 cursor-nesw-resize rounded-full border border-white bg-ai-primary"
                      aria-hidden="true"
                      onPointerDown={(event) => startCropInteraction(event, 'sw')}
                    />
                    <div
                      className="absolute -top-2 left-1/2 h-4 w-4 -translate-x-1/2 cursor-ns-resize rounded-full border border-white bg-ai-primary"
                      aria-hidden="true"
                      onPointerDown={(event) => startCropInteraction(event, 'n')}
                    />
                    <div
                      className="absolute bottom-0 left-1/2 h-4 w-4 -translate-x-1/2 translate-y-1/2 cursor-ns-resize rounded-full border border-white bg-ai-primary"
                      aria-hidden="true"
                      onPointerDown={(event) => startCropInteraction(event, 's')}
                    />
                    <div
                      className="absolute -top-2 right-0 h-4 w-4 translate-x-1/2 cursor-nesw-resize rounded-full border border-white bg-ai-primary"
                      aria-hidden="true"
                      onPointerDown={(event) => startCropInteraction(event, 'ne')}
                    />
                    <div
                      className="absolute top-1/2 right-0 h-4 w-4 translate-x-1/2 -translate-y-1/2 cursor-ew-resize rounded-full border border-white bg-ai-primary"
                      aria-hidden="true"
                      onPointerDown={(event) => startCropInteraction(event, 'e')}
                    />
                    <div
                      className="absolute bottom-0 right-0 h-4 w-4 translate-x-1/2 translate-y-1/2 cursor-nwse-resize rounded-full border border-white bg-ai-primary"
                      aria-hidden="true"
                      onPointerDown={(event) => startCropInteraction(event, 'se')}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-3">
              <button
                type="button"
                className="rounded-full border border-ai-surface-glow/40 px-6 py-2 text-sm font-semibold text-ai-text-secondary transition-colors hover:border-ai-primary/70 hover:text-ai-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
                onClick={closeCropper}
                disabled={isApplyingCrop}
              >
                {t('uploadPanel.cropper.cancel')}
              </button>
              <button
                type="button"
                className="rounded-full bg-ai-primary px-6 py-2 text-sm font-semibold text-white shadow-ai-glow transition-colors hover:bg-ai-secondary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus disabled:cursor-not-allowed disabled:opacity-70"
                onClick={applyCrop}
                disabled={isApplyingCrop}
              >
                {isApplyingCrop ? t('uploadPanel.cropper.processing') : t('uploadPanel.cropper.apply')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
